import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Divider,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Link
} from '@material-ui/core';

import { SBase } from '@setvi/shared/components';
import { Product } from '@setvi/shared/interfaces';

import { getProductMetadataQuery } from 'Services';
import { useAppContext } from 'Providers/AppProvider/AppContext';

import { Header } from './components';

interface MetadataProps {
  handleRegeneration?: (refresh: boolean) => void;
  onClose?: () => void;
  product: Product;
}

const isLink = (value: string) =>
  typeof value === 'string' &&
  (value.startsWith('http://') || value.startsWith('https://'));

// Dodajemo ključeve koji treba da se prikazuju kao liste
const listKeys = new Set([
  'product_features',
  'keywords',
  'alternate_names',
  'recommended_for'
]);

export const JsonViewer = ({
  data,
  level = 0,
  parentKey = ''
}: {
  data: any;
  level?: number;
  parentKey?: string;
}) => {
  if (data === null || data === undefined) return null;

  if (typeof data === 'string') {
    if (isLink(data)) {
      return (
        <Typography>
          <Link href={data} target="_blank" rel="noopener noreferrer">
            {data}
          </Link>
        </Typography>
      );
    }
    return <Typography>{data}</Typography>;
  }

  if (typeof data === 'number' || typeof data === 'boolean') {
    return <Typography>{String(data)}</Typography>;
  }

  if (Array.isArray(data)) {
    // Specifično renderovanje kao lista ako se ključ poklapa
    if (listKeys.has(parentKey)) {
      return (
        <List style={{ marginLeft: level * 2 }}>
          {data.map(item => (
            <ListItem key={String(item)}>
              <ListItemText primary={String(item)} />
            </ListItem>
          ))}
        </List>
      );
    }

    return (
      <Box ml={level * 2}>
        {data.map((item, idx) => (
          <Box key={JSON.stringify(item)} mb={1}>
            <Typography variant="subtitle2">[{idx}]</Typography>
            <JsonViewer data={item} level={level + 1} />
          </Box>
        ))}
      </Box>
    );
  }

  if (typeof data === 'object') {
    return (
      <Box ml={level * 2}>
        {Object.entries(data).map(([key, value]) => (
          <Box key={key} mb={1}>
            <Typography variant="subtitle2" color="primary">
              {key}
            </Typography>
            <JsonViewer data={value} level={level + 1} parentKey={key} />
          </Box>
        ))}
      </Box>
    );
  }

  return null;
};

const Metadata = ({ handleRegeneration, onClose, product }: MetadataProps) => {
  const { user } = useAppContext();

  const [duration, setDuration] = useState(0);
  const [forceRefresh, setForceRefresh] = useState(false);

  const params = useMemo(
    () => ({
      manufacturerName: product?.vendor?.name,
      manufacturerSku: user?.IsManufacturer
        ? product?.sku
        : product?.manufacturerSku,
      ...(product?.name && { productName: product?.name }),
      ...(forceRefresh && { forceRefresh: true })
    }),
    [user, product, forceRefresh]
  );

  const enabled = useMemo(() => {
    if (!params?.manufacturerName || !params?.manufacturerSku) return false;
    if (!user?.IsManufacturer && product?.sku === product?.manufacturerSku)
      return false;

    return true;
  }, [params, user, product]);

  const startTimeRef = useRef<number>(0);
  const metadataQuery = getProductMetadataQuery(params);

  const { data, isFetching } = useQuery({
    ...metadataQuery,
    queryFn: async () => {
      startTimeRef.current = Date.now();
      return metadataQuery.queryFn();
    },
    enabled,
    select: (res: any) => res?.result,
    onSettled: () => {
      if (startTimeRef.current) {
        const timeSpent = Date.now() - startTimeRef.current;
        setDuration(timeSpent);
      }
    }
  });

  const metadata = useMemo(() => {
    const { name, images, manufacturerSku, vendor, custom } = product;

    if (!isFetching && data) {
      if (forceRefresh) setForceRefresh(false);

      if (data?.for_review)
        return {
          name,
          images,
          manufacturerSku,
          vendor,
          custom
        };

      return data;
    }
    if (!isFetching && !data) {
      if (forceRefresh) setForceRefresh(false);

      return {
        name,
        images,
        manufacturerSku,
        vendor,
        custom
      };
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, product, isFetching]);

  const title = useMemo(() => {
    if (isFetching) return '';

    if (data) {
      // 100 ms is a around 0.1 seconds that needs state update to be triggered
      const seconds = (duration - 100 > 0 ? duration - 100 : 0) / 1000;
      const displayTime =
        seconds > 60
          ? `${(seconds / 60).toFixed(2)} mins`
          : `${seconds.toFixed(2)}s`;
      return `(Time: ${displayTime})`;
    }

    return `(Using client product info)`;
  }, [duration, data, isFetching]);

  return (
    <SBase width="90vw" height="90vh" disablePadding>
      <Header
        title={`Product Metadata ${title}`}
        loading={isFetching}
        onClose={onClose}
        onResetData={handleRegeneration}
        onResetMetadata={() => setForceRefresh(true)}
      />
      <Divider />
      {isFetching ? (
        <Box
          height="100%"
          width="100%"
          display="flex"
          justifyContent="center"
          alignItems="center">
          <CircularProgress />
        </Box>
      ) : (
        <Box p={4} maxHeight="80vh" overflow="auto">
          <JsonViewer data={metadata} />
        </Box>
      )}
    </SBase>
  );
};

export default Metadata;
